import React, { useMemo } from 'react'
import { formatPrice } from '../../services/plans/index'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import CountdownTimer, { FORMAT_HH_MM_SS, FORMAT_DAYS_HRS_MINS } from '../CountdownTimer/index'

export const OFFER_DISPLAY_TYPE_CARD = 'card'
export const OFFER_DISPLAY_TYPE_PROMO = 'promo'

/**
 * The special offer card view component
 * @param {Map} props.staticText - The static text
 * @param {Object} props.billingData - The biling plan data
 * @param {Object} props.offerLink - The offer link url and query data
 * @param {Number} props.durationMs - The offer timer value in ms
 * @param {Function} props.onOfferClick - The offer button click handler
 * @returns {JSX.Element} special offer component
 */
function OfferCard (props) {
  const {
    staticText,
    billingData,
    offerLink = {},
    durationMs,
    onOfferClick,
  } = props

  return (
    <div className="share-page__offer-card">
      <div className="share-page__offer-card-content">
        <p className="share-page__offer-card-content-title1">
          🔥 {staticText.get('limitedTimeOffer')}
        </p>
        <p className="share-page__offer-card-content-title2">
          {staticText.get('threeMonths')}
          <br />
          {!!billingData.firstPayment && staticText.get('for').replace(/\$\{ firstPayment \}/, billingData.firstPayment)}
        </p>
        <p className="share-page__offer-card-content-text">
          {!!billingData.savingPercentage && !!billingData.regularPrice && staticText.get('savings')
            .replace(/\$\{ savingPercentage \}/, billingData.savingPercentage)
            .replace(/\$\{ regularPrice \}/, billingData.regularPrice)
          }
        </p>
        <ul className="share-page__offer-card-content-props">
          <li>{staticText.get('valueProp1')}</li>
          <li>{staticText.get('valueProp2')}</li>
          <li>{staticText.get('valueProp3')}</li>
        </ul>
        <Button
          className="share-page__offer-card-button"
          type={BUTTON_TYPES.PRIMARY}
          onClick={onOfferClick}
          {...offerLink}
        >
          {!!billingData.firstPayment && staticText.get('getThreeMonths').replace(/\$\{ firstPayment \}/, billingData.firstPayment)}
        </Button>
        {billingData.firstPayment
          ? <p className="share-page__offer-card-content-billing">
            {billingData.monthlyPrice && staticText.get('thenJust').replace(/\$\{ monthlyPrice \}/, billingData.monthlyPrice)}<br />
            <b>{staticText.get('cancelAnytime')}</b>
          </p>
          : null
        }
      </div>
      <div className="share-page__offer-card-footer">
        <p className="share-page__offer-card-footer-text">
          {staticText.get('expiresIn').replace(/\$\{ time \}/, '')}
          <CountdownTimer durationMs={durationMs} format={FORMAT_HH_MM_SS}/>
        </p>
      </div>
    </div>
  )
}

/**
 * The special offer promo view component
 * @param {Map} props.staticText - The static text
 * @param {Object} props.billingData - The biling plan data
 * @param {Object} props.offerLink - The offer link url and query data
 * @param {Number} props.durationMs - The offer timer value in ms
 * @param {Function} props.onOfferClick - The offer button click handler
 * @returns {JSX.Element} special offer component
 */
function OfferPromo (props) {
  const {
    staticText,
    billingData,
    offerLink = {},
    durationMs,
    onOfferClick,
    language,
  } = props

  const promoClassName = [
    'share-page__offer-promo',
    `share-page__offer-promo--${language.toLowerCase()}`,
  ].join(' ')

  return (
    <div className={promoClassName}>
      <div className="share-page__offer-promo-label">
        {staticText.get('offerLabel')}
      </div>
      <p className="share-page__offer-promo-title">
        {staticText.get('offerTitleLarge')}
      </p>
      <p className="share-page__offer-promo-text">
        {staticText.get('offerTextPart1')} <span>{staticText.get('offerTextPart2')}</span> {staticText.get('offerTextPart3')}
      </p>
      <div className="share-page__offer-promo-cta">
        <Button
          className="share-page__offer-promo-button"
          type={BUTTON_TYPES.PRIMARY}
          onClick={onOfferClick}
          {...offerLink}
        >
          {staticText.get('claimMySpecialOffer')}
        </Button>
        <div className="share-page__offer-promo-billing">
          <p className="share-page__offer-promo-price">
            {billingData.firstPayment && billingData.firstPayment
              && <>
                <span>{billingData.regularPrice}</span>
                <b>{` ${billingData.firstPayment} `}</b>
                {staticText.get('foThreeMonths')}
              </>
            }
          </p>
        </div>
      </div>
      <p className="share-page__offer-promo-cowntdown">
        {staticText.get('endsIn').replace(/\$\{ time \}/, '')}
        <CountdownTimer
          durationMs={durationMs}
          format={FORMAT_DAYS_HRS_MINS}
          staticText={staticText}
        />
      </p>
    </div>
  )
}

/**
 * The special offer component
 * @param {Map} props.staticText - The static text
 * @param {Map} props.offerPlan - The biling plan data
 * @param {Object} props.offerLink - The offer link url and query data
 * @param {String} props.expirationDate - The offer expiration date
 * @param {String} props.bgImageUrl - The promo view background image url
 * @param {Function} props.onOfferClick - The offer button click handler
 * @returns {JSX.Element} special offer component
 */
function SharePageSpecialOffer (props) {
  const {
    staticText,
    offerPlan,
    offerLink = {},
    expirationDate,
    displayType = OFFER_DISPLAY_TYPE_CARD,
    onOfferClick,
    language,
  } = props

  const durationMs = expirationDate ? new Date(expirationDate).getTime() - Date.now() : 0

  const billingData = useMemo(() => {
    const currencyIso = offerPlan.get('currencyIso')
    const monthlyPriceVal = offerPlan.getIn(['costs', '1'], '0')
    const firstPaymentVal = offerPlan.getIn(['costs', '0'], '0')
    const offerPriceVal = Math.floor((parseFloat(firstPaymentVal) / 3) * 100) / 100
    const monthlyPrice = formatPrice(currencyIso, monthlyPriceVal, false, null, language)
    const firstPayment = formatPrice(currencyIso, firstPaymentVal, false, null, language)
    const offerPrice = formatPrice(currencyIso, offerPriceVal, false, null, language)
    const regularPriceVal = parseFloat(monthlyPriceVal * 3)
    const regularPrice = formatPrice(currencyIso, regularPriceVal, false, null, language)
    const savingPercentage = Math.round((1 - firstPaymentVal / regularPriceVal) * 100)

    return {
      monthlyPrice,
      firstPayment,
      offerPrice,
      regularPrice,
      savingPercentage,
    }
  }, [offerPlan])

  return displayType === OFFER_DISPLAY_TYPE_PROMO
    ? (<OfferPromo
      staticText={staticText}
      billingData={billingData}
      offerLink={offerLink}
      durationMs={durationMs}
      onOfferClick={onOfferClick}
      language={language}
    />)
    : (<OfferCard
      staticText={staticText}
      billingData={billingData}
      offerLink={offerLink}
      durationMs={durationMs}
      onOfferClick={onOfferClick}
    />)
}

export default SharePageSpecialOffer
